// Universal ceiling — no upload anywhere exceeds this
export const MAX_FILE_SIZE = 60 * 1024 * 1024; // 60 MB

// Per file-type default limits
export const FILE_TYPE_LIMITS = {
  image:    { maxFileSize: 25 * 1024 * 1024 }, // JPEG, PNG, WEBP, HEIC — client compresses first
  document: { maxFileSize: 60 * 1024 * 1024 }, // PDF, DOC/DOCX, XLS/XLSX, TXT, HTML, ESX
  audio:    { maxFileSize: 60 * 1024 * 1024 }, // WAV, MP3, OGG, M4A, WEBM audio
  video:    { maxFileSize: 60 * 1024 * 1024 }, // WEBM video (browser-recorded)
} as const;

// Context-specific lower limits that override the per-type defaults above
export const CONTEXT_SIZE_OVERRIDES = {
  checkImage:    10 * 1024 * 1024, // carrier check extraction — clarity over size
  publicLeadPdf: 10 * 1024 * 1024, // public-facing intake form — conservative
  branding:       5 * 1024 * 1024, // logo upload — small by design
  ragPdf:        20 * 1024 * 1024, // RAG knowledge base — text extraction limit
  emailAttach:   25 * 1024 * 1024, // outbound email attachments
} as const;

export const ALLOWED_MIME_TYPES = [
  'application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
  'image/webp', 'image/heic', 'image/heif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain', 'text/html', 'text/csv',
  'audio/webm', 'audio/wav', 'audio/mpeg', 'audio/mp3', 'audio/mp4',
  'audio/m4a', 'audio/ogg', 'video/webm', 'video/mp4', 'video/quicktime',
  'application/x-esx',
  'message/rfc822',
] as const;

export const IMAGE_MIME_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp',
  'image/heic', 'image/heif', 'image/gif',
] as const;

/** ZIP-based Office formats that file-type detects as 'application/zip' */
export const ZIP_BASED_MIMES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/x-esx',
] as const;

// Client- and server-side compression settings
export const IMAGE_COMPRESSION = {
  thresholdBytes:   1 * 1024 * 1024, // only compress images larger than this
  maxWidthOrHeight: 2048,
  jpegQuality:      82,
  maxSizeMB:        2,               // browser-image-compression target
} as const;

/** Returns the effective max file size for a MIME type + optional context override. */
export function getMaxFileSize(
  mimeType: string,
  context?: keyof typeof CONTEXT_SIZE_OVERRIDES
): number {
  if (context && CONTEXT_SIZE_OVERRIDES[context]) return CONTEXT_SIZE_OVERRIDES[context];
  if ((IMAGE_MIME_TYPES as readonly string[]).includes(mimeType)) return FILE_TYPE_LIMITS.image.maxFileSize;
  const AUDIO_MIMES = ['audio/webm', 'audio/wav', 'audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/m4a', 'audio/ogg'];
  if (AUDIO_MIMES.includes(mimeType)) return FILE_TYPE_LIMITS.audio.maxFileSize;
  if (mimeType === 'video/webm' || mimeType === 'video/mp4' || mimeType === 'video/quicktime') return FILE_TYPE_LIMITS.video.maxFileSize;
  return FILE_TYPE_LIMITS.document.maxFileSize;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
